import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, Loader2, MessageSquare } from 'lucide-react';
import { chatSessionService, type ChatSession, type ChatSessionSummary } from '@/services';
import { attachmentService } from '@/services';
import { useChatStream } from './useChatStream';
import { ChatInput, type ChatInputHandle } from './ChatInput';
import { SessionActionsDropdown } from './SessionActionsDropdown';
import type { ChatMessage, ChatPanelProps, MessageContent } from './types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export function ChatPanel({ entityType, entityId, companyId, userId, systemPrompt, attachmentFilePath, sessionType = 'briefing', title = 'Briefing Assistant', emptyStateText = 'Ask questions about this price book\'s terms and domain knowledge.', icon }: ChatPanelProps) {
  const Icon = icon || MessageSquare;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const sessionRef = useRef<ChatSession | null>(null);
  const creatingSessionRef = useRef(false);
  const pendingPersistRef = useRef<ChatMessage[] | null>(null);
  const pdfBase64Ref = useRef<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<ChatInputHandle>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Download PDF attachment on mount / when path changes
  useEffect(() => {
    if (!attachmentFilePath) {
      pdfBase64Ref.current = null;
      return;
    }
    let cancelled = false;
    setPdfLoading(true);
    attachmentService.downloadAsBase64(attachmentFilePath).then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        console.error('Failed to download PDF for chat:', error);
      }
      pdfBase64Ref.current = data;
      setPdfLoading(false);
    });
    return () => { cancelled = true; };
  }, [attachmentFilePath]);

  // Load active session (most recently modified)
  const { isLoading: sessionLoading } = useQuery({
    queryKey: ['chat-session', entityType, entityId, sessionType],
    queryFn: async () => {
      const result = await chatSessionService.getActiveByEntity(entityType, entityId, sessionType);
      if (result.error) throw result.error;
      if (result.data) {
        sessionRef.current = result.data;
        const history = result.data.conversation_history as ChatMessage[];
        if (Array.isArray(history) && history.length > 0) {
          setMessages(history);
        }
      }
      return result.data;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
  });

  // Sessions list – only fetched when the dropdown opens
  const { data: sessionsList = [], refetch: refetchSessionsList } = useQuery<ChatSessionSummary[]>({
    queryKey: ['chat-sessions-list', entityType, entityId, sessionType],
    queryFn: async () => {
      const result = await chatSessionService.listByEntity(entityType, entityId, sessionType);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: false,
  });

  // Auto-scroll when streaming or new messages
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    if (isNearBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [streamingContent, messages]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollButton(distanceFromBottom > 100);
  }, []);

  // Check scroll button visibility when messages load (e.g. existing session)
  useEffect(() => {
    // Defer to next frame so the DOM has rendered the messages
    const raf = requestAnimationFrame(() => handleScroll());
    return () => cancelAnimationFrame(raf);
  }, [messages, handleScroll]);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    chatInputRef.current?.focus();
  }, []);

  const persistConversation = useCallback(async (updatedMessages: ChatMessage[]) => {
    const context = { companyId, userId };
    const history = updatedMessages.map(m => ({
      id: m.id,
      role: m.role,
      content: m.content,
      timestamp: m.timestamp,
    }));

    if (sessionRef.current) {
      await chatSessionService.updateConversation(sessionRef.current.id, history, context);
    } else {
      if (creatingSessionRef.current) return;
      creatingSessionRef.current = true;
      try {
        const result = await chatSessionService.create(entityType, entityId, context, sessionType);
        if (result.data) {
          sessionRef.current = result.data;
          await chatSessionService.updateConversation(result.data.id, history, context);
        }
      } finally {
        creatingSessionRef.current = false;
      }
    }
  }, [companyId, userId, entityType, entityId, sessionType]);

  const onToken = useCallback((text: string) => {
    setStreamingContent(prev => prev + text);
  }, []);

  const onDone = useCallback(() => {
    setStreamingContent(prev => {
      if (prev) {
        const assistantMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: prev,
          timestamp: new Date().toISOString(),
        };
        setMessages(current => {
          const updated = [...current, assistantMsg];
          pendingPersistRef.current = updated;
          return updated;
        });
      }
      return '';
    });
    setIsStreaming(false);
  }, []);

  const onError = useCallback((errorMsg: string) => {
    setError(errorMsg);
    // Still save any partial streaming content
    setStreamingContent(prev => {
      if (prev) {
        const assistantMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: prev,
          timestamp: new Date().toISOString(),
        };
        setMessages(current => {
          const updated = [...current, assistantMsg];
          pendingPersistRef.current = updated;
          return updated;
        });
      }
      return '';
    });
    setIsStreaming(false);
  }, []);

  // Persist conversation after state updates settle
  useEffect(() => {
    if (pendingPersistRef.current) {
      const msgs = pendingPersistRef.current;
      pendingPersistRef.current = null;
      persistConversation(msgs);
    }
  }, [messages, persistConversation]);

  const { sendMessage, cancel } = useChatStream({ onToken, onDone, onError });

  // Cleanup on unmount
  useEffect(() => {
    return () => cancel();
  }, [cancel]);

  const handleSend = useCallback((content: string) => {
    setError(null);
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };

    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setIsStreaming(true);
    setStreamingContent('');

    const apiMessages: { role: 'user' | 'assistant'; content: MessageContent }[] =
      updatedMessages.map(m => ({ role: m.role, content: m.content as MessageContent }));

    // Inject PDF document block into the first message so the model can analyze it
    if (pdfBase64Ref.current && apiMessages.length > 0) {
      const first = apiMessages[0];
      const textContent = typeof first.content === 'string' ? first.content : '';
      apiMessages[0] = {
        ...first,
        content: [
          { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: pdfBase64Ref.current } },
          { type: 'text', text: textContent },
        ],
      };
    }

    sendMessage(apiMessages, systemPrompt, entityId);
  }, [messages, sendMessage, systemPrompt, entityId]);

  const handleStop = useCallback(() => {
    cancel();
  }, [cancel]);

  const handleNewChat = useCallback(() => {
    cancel();
    setMessages([]);
    setStreamingContent('');
    setError(null);
    sessionRef.current = null;
  }, [cancel]);

  const handleSwitchSession = useCallback(async (sessionId: string) => {
    if (sessionRef.current?.id === sessionId) return;
    cancel();
    const result = await chatSessionService.getById(sessionId);
    if (result.error || !result.data) {
      setError('Failed to load session');
      return;
    }
    sessionRef.current = result.data;
    const history = result.data.conversation_history as ChatMessage[];
    setMessages(Array.isArray(history) ? history : []);
    setStreamingContent('');
    setError(null);
  }, [cancel]);

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 shrink-0">
        <Icon className="w-4 h-4 text-primary" />
        <span className="text-xs font-medium flex-1">{title}</span>
        <SessionActionsDropdown
          currentSessionId={sessionRef.current?.id ?? null}
          sessionsList={sessionsList}
          onNewChat={handleNewChat}
          onSwitchSession={handleSwitchSession}
          onOpenDropdown={() => refetchSessionsList()}
          isStreaming={isStreaming}
        />
      </div>

      {/* Messages */}
      <div className="relative flex-1 overflow-hidden">
        {showScrollButton && (
          <Button
            size="icon"
            variant="secondary"
            onClick={scrollToBottom}
            className="absolute top-2 left-1/2 -translate-x-1/2 z-10 h-7 w-7 rounded-full shadow-md opacity-90 hover:opacity-100"
          >
            <ArrowDown className="w-4 h-4" />
          </Button>
        )}
        <div ref={scrollContainerRef} onScroll={handleScroll} className="h-full overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && !isStreaming && (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2 px-4">
            {pdfLoading ? (
              <>
                <Loader2 className="w-8 h-8 opacity-40 animate-spin" />
                <p className="text-xs text-center">Loading price book PDF...</p>
              </>
            ) : (
              <>
                <Icon className="w-8 h-8 opacity-40" />
                <p className="text-xs text-center">
                  {emptyStateText}
                </p>
              </>
            )}
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={cn(
              'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
              msg.role === 'user'
                ? 'ml-auto bg-primary text-primary-foreground'
                : 'mr-auto bg-muted'
            )}
          >
            {msg.content}
          </div>
        ))}

        {/* Streaming message */}
        {isStreaming && (
          <div className="mr-auto max-w-[85%] rounded-lg px-3 py-2 text-sm bg-muted whitespace-pre-wrap">
            {streamingContent || (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            )}
            {streamingContent && (
              <span className="inline-block w-1.5 h-4 ml-0.5 bg-foreground/70 animate-pulse align-text-bottom" />
            )}
          </div>
        )}

        {/* Error display */}
        {error && (
          <div className="mr-auto max-w-[85%] rounded-lg px-3 py-2 text-sm bg-destructive/10 text-destructive border border-destructive/20">
            {error}
          </div>
        )}

        <div ref={bottomRef} />
        </div>
      </div>

      {/* Input */}
      <ChatInput
        ref={chatInputRef}
        onSend={handleSend}
        onStop={handleStop}
        isStreaming={isStreaming}
        disabled={pdfLoading}
      />
    </div>
  );
}
